import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import HelpIcon from '@material-ui/icons/Help';
import ReportIcon from '@material-ui/icons/Report';
import {
  ApiFilterOperation,
  DetailContext,
  DetailMode,
  DictionaryObject,
  EvidenceContext,
  FormPanel,
  PagedTableField,
  TableFieldHandle,
  TableFieldSpeedDialAction,
  TableFieldToolbarHandle,
  TableFieldToolbarProps,
} from '@eas/common-web';
import { useDownloadAttachmentsZip } from '@composite/attachments/actions/download-attachments-zip-hook';
import { FormTableCount } from '@components/form/table-field/form-table-count';
import { DeliveredDocument, Document, OutgoingDocument } from '@models';
import { Agenda, DocumentType, EvidenceApiUrl, UIElement } from '@enums';
import { useColumns } from './document-columns';
import { DocumentContext } from './document-context';
import { DocumentTableFieldRow } from './document-table-field-row';
import { DocumentToolbar } from './document-toolbar';

export function DocumentField(props: {
  documentTypes: DictionaryObject[];
  documentDeliveryMethods: DictionaryObject[];
  documentDispatchStates: DictionaryObject[];
  hasPermission: (element: string) => boolean;
}) {
  const intl = useIntl();

  const { hookedSources } = useContext(EvidenceContext);
  const { isLocked, mode, source } = useContext(DetailContext);

  const [openState, setOpenState] = useState({});

  useEffect(() => {
    setOpenState({});
  }, [source.data]);

  const maxRows = 50;
  const preFilters = useMemo(
    () => [
      {
        field: 'esFile.id',
        operation: ApiFilterOperation.EQ,
        value: source.data.id,
      },
    ],
    [source.data.id]
  );

  const documentSource = hookedSources['DOCUMENT_SOURCE']?.source;

  const { callApi: downloadAttachmentsZipApiCall } = useDownloadAttachmentsZip({
    url: source.data?.id
      ? `${EvidenceApiUrl.ES_FILES}/${source.data.id}/document-attachments.zip`
      : undefined,
  });

  const hasDownloadableAttachments = documentSource?.items?.some(
    (document: Document) =>
      document.attachments?.some((attachment) => !!attachment.file?.id)
  );

  const columns = useColumns({
    ...props,
    onDownloadAttachments: downloadAttachmentsZipApiCall,
    downloadAttachmentsDisabled: !hasDownloadableAttachments,
  });

  const tableRef = useRef<TableFieldHandle>(null);
  const toolbarRef = useRef<TableFieldToolbarHandle>(null);
  const toolbarConfigRef = useRef<{
    disabled: boolean;
    preFilters: typeof preFilters;
    documentTypes: DictionaryObject[];
    documentDeliveryMethods: DictionaryObject[];
    documentDispatchStates: DictionaryObject[];
    hasPermission: (element: string) => boolean;
  } | null>(null);

  const disabled = isLocked || mode !== DetailMode.VIEW;

  toolbarConfigRef.current = {
    disabled,
    preFilters,
    documentTypes: props.documentTypes,
    documentDeliveryMethods: props.documentDeliveryMethods,
    documentDispatchStates: props.documentDispatchStates,
    hasPermission: props.hasPermission,
  };

  const Toolbar = useCallback((toolbarProps: TableFieldToolbarProps) => {
    const {
      disabled,
      preFilters,
      documentTypes,
      documentDeliveryMethods,
      documentDispatchStates,
      hasPermission,
    } = toolbarConfigRef.current!;

    return (
      <DocumentToolbar
        {...toolbarProps}
        ref={toolbarRef}
        disabled={disabled}
        preFilters={preFilters}
        documentTypes={documentTypes}
        documentDeliveryMethods={documentDeliveryMethods}
        documentDispatchStates={documentDispatchStates}
        hasPermission={hasPermission}
      />
    );
  }, []);

  const actions: TableFieldSpeedDialAction<Document>[] = useMemo(
    () => [
      {
        Icon: ReportIcon,
        name: intl.formatMessage({
          id: 'ES__DOCUMENTS__SET_COMPLAINT',
          defaultMessage: 'Nastavit stížnost',
        }),
        onClick: async (_, index) => {
          await tableRef.current?.setSelectedIndex(index);
          toolbarRef.current?.executeAction('SET_COMPLAINT');
        },
        show: (document) => {
          return (
            document.agenda === Agenda.INF &&
            document.type === DocumentType.DELIVERED &&
            !(document as DeliveredDocument).complaint &&
            props.hasPermission(UIElement.Document.SET_COMPLAINT_BUTTON)
          );
        },
      },
      {
        Icon: ReportIcon,
        name: intl.formatMessage({
          id: 'ES__DOCUMENTS__REMOVE_COMPLAINT',
          defaultMessage: 'Odebrat stížnost',
        }),
        onClick: async (_, index) => {
          await tableRef.current?.setSelectedIndex(index);
          toolbarRef.current?.executeAction('REMOVE_COMPLAINT');
        },
        show: (document) => {
          return (
            document.agenda === Agenda.INF &&
            document.type === DocumentType.DELIVERED &&
            !!(document as DeliveredDocument).complaint &&
            props.hasPermission(UIElement.Document.SET_COMPLAINT_BUTTON)
          );
        },
      },
      {
        Icon: HelpIcon,
        name: intl.formatMessage({
          id: 'ES__DOCUMENTS__SET_DECISION',
          defaultMessage: 'Nastavit správní rozhodnutí',
        }),
        onClick: async (_, index) => {
          await tableRef.current?.setSelectedIndex(index);
          toolbarRef.current?.executeAction('SET_DECISION');
        },
        show: (document) => {
          return (
            document.agenda === Agenda.INF &&
            document.type === DocumentType.OUTGOING &&
            !(document as OutgoingDocument).decision &&
            props.hasPermission(UIElement.Document.SET_DECISION_BUTTON)
          );
        },
      },
      {
        Icon: HelpIcon,
        name: intl.formatMessage({
          id: 'ES__DOCUMENTS__REMOVE__DECISION',
          defaultMessage: 'Odebrat správní rozhodnutí',
        }),
        onClick: async (_, index) => {
          await tableRef.current?.setSelectedIndex(index);
          toolbarRef.current?.executeAction('REMOVE_DECISION');
        },
        show: (document) => {
          return (
            document.agenda === Agenda.INF &&
            document.type === DocumentType.OUTGOING &&
            !!(document as OutgoingDocument).decision &&
            props.hasPermission(UIElement.Document.SET_DECISION_BUTTON)
          );
        },
      },
    ],
    [intl]
  );

  if (!documentSource) {
    return <> Missing DOCUMENT_FIELD_SOURCE </>;
  }

  const showSpeedDial = (document: Document) => {
    if (isLocked) return false;

    return actions.some((action) => action.show?.(document, -1) ?? true);
  };

  return (
    <>
      <FormPanel
        label={
          <FormattedMessage
            id="ES__DOCUMENTS__PANEL_TITLE"
            defaultMessage="Dokumenty"
          />
        }
        summary={<FormTableCount source={documentSource} />}
      >
        <DocumentContext.Provider value={{ openState, setOpenState }}>
          <PagedTableField<Document>
            ref={tableRef}
            columns={columns}
            ToolbarComponent={Toolbar}
            speedDialActions={actions}
            maxRows={maxRows}
            rowsPerPageOptions={[25, 50, 100]}
            source={documentSource}
            showDetailBtnCond={() => false}
            showRadioCond={() => false}
            showSpeedDialCond={showSpeedDial}
            RowComponent={DocumentTableFieldRow}
            disabled={disabled}
            createRowStyle={(row) =>
              row?.type === DocumentType.DELIVERED &&
              (row as DeliveredDocument).removedFromEsFile
                ? {
                    textDecoration: 'line-through',
                  }
                : {}
            }
          />
        </DocumentContext.Provider>
      </FormPanel>
    </>
  );
}
